import z from 'zod';
import { emailSchema } from '../../../schemas/others';
import { phoneSchema } from '../../../schemas/validation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginUser, loginUserByPhone } from '../signin';
import { useCallback } from 'react';
import {
  clearTemporalSession,
  getTemporalSession,
  setTemporalSession,
} from '../../../utils/tempSession';
import { setUserSession } from '../../../store/slices/UserSession/userSession';
import React from 'react';
import useIonicToast from '../../../hooks/useIonicToast';
import { usePageLoader } from '../../../context/pageLoader';
import { useDispatch } from 'react-redux';
import { useDialog } from '../../../context/Dialog';
import RoleChangeDialogue from '../../../components/auth-components/signin/RoleChangeDialog/RoleChangeDialogue';
import { useHistory, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

type RequestBody = {
  email: string;
  password: string;
  role_type: 'store_owner';
};

type PhoneRequestBody = {
  phone: string;
  password: string;
  role_type: 'store_owner';
};

export type LoginMode = 'email' | 'phone';

export default function () {
  const history = useHistory();
  const location = useLocation();
  const { presentIonToast } = useIonicToast();
  const { setBusy } = usePageLoader();
  const dispatch = useDispatch();
  const { openDialog } = useDialog();
  const queryClient = useQueryClient();

  // LOGIN FORM SCHEMA - Both email and phone optional for now, validated based on mode
  const loginFormSchema = z
    .object({
      email: emailSchema.optional(),
      phone: phoneSchema.optional(),
      password: z.string().min(1, {
        message: 'Password is required',
      }),
    })
    .refine(
      (data) => {
        // At least one identifier (email or phone) must be provided
        return data.email || data.phone;
      },
      {
        message: 'Email or phone is required',
        path: ['email'],
      }
    );

  //REACT FORM HOOK OBJECT
  const formHandler = useForm<z.infer<typeof loginFormSchema>>({
    resolver: zodResolver(loginFormSchema),
  });

  //SHOW ROLE CHANGE DIALOG
  const displayChangeDialog = () => {
    const signinInfo = formHandler.getValues();

    if (!signinInfo.email) return;
    openDialog(
      <RoleChangeDialogue
        email={signinInfo.email}
        password={signinInfo.password}
      />
    );
  };

  //HANDLE UNVERIFIED USERS
  const handleUnverifiedUsers = useCallback(
    (userInfo: RequestBody & { isVerified: boolean }) => {
      setTemporalSession({ ...userInfo, isVerified: false });
      history.push('/auth/sign-up?step=2');
      presentIonToast('Please verify your account to continue', 'error');
    },
    []
  );

  //CHANGE USER ROLE TO "STORE_OWNER"
  const handleUsersRoleChange = useCallback(() => {
    displayChangeDialog();
    return;
  }, []);

  //PROCEED TO USER DASHBOARD IF SIGNIN WAS SUCCESSFUL
  const proceedToDashboard = (responseData: any) => {
    // A genuine sign-in MUST carry an access token. Without one, dispatching a
    // session leaves the app "logged in" with a null token and every request
    // fails. Surface the message instead of a silent broken state.
    if (!responseData?.signedAccessToken) {
      presentIonToast(
        responseData?.details ||
          responseData?.message ||
          'Unable to sign in. Please try again.',
        'error'
      );
      return;
    }

    clearTemporalSession();
    dispatch(setUserSession(responseData));
    history.replace('/dashboard');
  };

  //login logic for api request
  const login = async (
    mode: LoginMode,
    identifier: string,
    password: string
  ) => {
    try {
      setBusy(true);
      queryClient.clear();
      const trimmedIdentifier = identifier.trim();
      const requestBody: RequestBody | PhoneRequestBody =
        mode === 'phone'
          ? {
              phone: trimmedIdentifier,
              password,
              role_type: 'store_owner' as const,
            }
          : {
              email: trimmedIdentifier.toLowerCase(),
              password,
              role_type: 'store_owner' as const,
            };

      const responseData =
        mode === 'phone'
          ? await loginUserByPhone(requestBody as PhoneRequestBody)
          : await loginUser(requestBody as RequestBody);

      const userInfo = {
        email: mode === 'email' ? trimmedIdentifier.toLowerCase() : '',
        password,
        role_type: 'store_owner' as const,
        isVerified: true,
      };
      queryClient.clear();
      if (responseData.code === 230) return handleUnverifiedUsers(userInfo);
      if (responseData.code === 231) {
        setTemporalSession({
          isVerified: false,
          email:
            mode === 'email'
              ? trimmedIdentifier.toLowerCase()
              : (responseData?.user?.email as string) || '',
          phone:
            mode === 'phone'
              ? trimmedIdentifier
              : (responseData?.user?.phone as string) || '',
          password,
          role_type: 'store_owner',
        });
        history.push('/auth/sign-up?step=2');
        presentIonToast('Verify your phone number to continue', 'error');
        return;
      } else if (responseData.code === 220) return handleUsersRoleChange();
      else return proceedToDashboard(responseData);
    } catch (error: any) {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      console.error(error.message);
      if ([401, 400].includes(error.status as number)) {
        presentIonToast('Invalid credentials', 'error');
      } else presentIonToast(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Handle form submission
  const handleFormSubmission = (mode: LoginMode) =>
    formHandler.handleSubmit((formData) => {
      const identifier = mode === 'phone' ? formData.phone : formData.email;
      if (!identifier) return;

      login(mode, identifier, formData.password);
    });

  const handleGoogleRedirect = () => {
    //CHECK IF A TEMPORAL SESSION EXIST
    const tempSession = getTemporalSession();
    if (
      !tempSession ||
      !('isGoogleUser' in tempSession) ||
      !tempSession.isGoogleUser
    )
      return;

    const email = tempSession.email! || '';
    const password = tempSession.password! || '';
    formHandler.setValue('email', email);
    formHandler.setValue('password', password);

    //CHANGE USER ROLE
    handleUsersRoleChange();
  };

  React.useEffect(() => {
    formHandler.reset();
    handleGoogleRedirect();
  }, [location]);

  return {
    formHandler,
    handleFormSubmission,
  };
}
